import React, { useState, useEffect } from 'react';
import { SchemaVersionsResponse } from '../types';
import { adminFormsService } from '../services/admin-forms.service';
import { COLORS } from '../../../../core/constants/colors';

interface SchemaVersionsDrawerProps {
  schemaId: number | null;
  isOpen: boolean;
  onClose: () => void;
}

export const SchemaVersionsDrawer: React.FC<SchemaVersionsDrawerProps> = ({
  schemaId,
  isOpen,
  onClose,
}) => {
  const [versions, setVersions] = useState<SchemaVersionsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && schemaId) {
      loadVersions();
    } else {
      setVersions(null);
      setError('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, schemaId]);

  const loadVersions = async () => {
    if (!schemaId) return;
    setIsLoading(true);
    setError('');
    try {
      const data = await adminFormsService.getSchemaVersions(schemaId);
      if (data) {
        setVersions(data);
      } else {
        setError('Failed to load versions');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load versions');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Side Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-6 py-5 border-b flex items-center justify-between flex-shrink-0" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold" style={{ color: '#1F2937' }}>
              Schema Versions
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 px-6 py-6 overflow-y-auto">
            {isLoading && (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-transparent mb-2"
                  style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
                />
                <p className="text-sm" style={{ color: '#6B7280' }}>Loading versions...</p>
              </div>
            )}

            {error && (
              <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: '#FEE2E2' }}>
                <p className="text-sm" style={{ color: '#DC2626' }}>{error}</p>
              </div>
            )}

            {versions && (
              <div className="space-y-4">
                <div className="mb-4">
                  <p className="text-sm" style={{ color: '#6B7280' }}>
                    Total versions: <span className="font-semibold" style={{ color: '#1F2937' }}>{versions.total}</span>
                  </p>
                </div>

                {versions.versions.length === 0 ? (
                  <div className="text-center py-12">
                    <svg className="w-12 h-12 mx-auto mb-3" style={{ color: '#D1D5DB' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm" style={{ color: '#9CA3AF' }}>No versions found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {versions.versions.map((version) => (
                      <div
                        key={version.id}
                        className="border rounded-lg p-4"
                        style={{ borderColor: '#E5E7EB' }}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="px-3 py-1 rounded-full text-sm font-semibold text-white"
                                style={{ backgroundColor: COLORS.admin.primary }}
                              >
                                Version {version.version_number}
                              </span>
                              <span className="text-xs" style={{ color: '#6B7280' }}>
                                {version.question_ids.length} questions
                              </span>
                            </div>
                            {version.changes_description && (
                              <p className="text-sm mt-2" style={{ color: '#6B7280' }}>
                                {version.changes_description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-xs pt-3 border-t" style={{ borderColor: '#E5E7EB', color: '#9CA3AF' }}>
                          <span>
                            Created: {new Date(version.created_at).toLocaleString()}
                          </span>
                          {version.created_by && (
                            <span>
                              By: {version.created_by}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

